/**
 * Bulk Creation report codes.
 *
 * The bulk job has no Shop Language for messages, so every report item
 * carries stable codes plus params (file name, product name); the web
 * translates them under `seller.bulk.*`. Adding a code means adding its
 * translation to every locale file.
 */

export const BULK_REPORT_CODES = [
  // The row became a listing
  'row_created',
  // Notes on a created row
  'ai_fallback',
  'color_dropped',
  'product_dropped',
  'publish_failed',
  'mockups_incomplete',
  'published',
  // The row failed or its listing is gone
  'asset_not_found',
  'qc_failed',
  // A product of the row is not selectable, or a position of the row is
  // not configured (print-area calibration, R13/R31)
  'product_uncalibrated',
  'position_uncalibrated',
  // Any other failure while creating the row (nothing was kept)
  'row_failed',
  'deleted_by_seller',
  'stalled',
] as const;

export type BulkReportCode = (typeof BULK_REPORT_CODES)[number];

export function isBulkReportCode(value: unknown): value is BulkReportCode {
  return (
    typeof value === 'string' &&
    (BULK_REPORT_CODES as readonly string[]).includes(value)
  );
}

/**
 * Result of one row: created, created with a note (AI fallback, dropped
 * colour or product, failed publish), or failed without a listing.
 */
export const BULK_ROW_OUTCOMES = [
  'created',
  'created_with_notes',
  'failed',
] as const;

export type BulkRowOutcome = (typeof BULK_ROW_OUTCOMES)[number];

// ─── Limits (R56) ────────────────────────────────────────────────────

/** Rows per bulk run; the web blocks the 101st row before uploading. */
export const BULK_MAX_ROWS = 100;

/** Products a run may take from its reference (DTO cap). */
export const BULK_MAX_PRODUCTS = 50;

/** Concurrently queued or running bulk jobs per seller. */
export const BULK_MAX_ACTIVE_JOBS = 3;

/** Longest working title of a row. */
export const BULK_TITLE_MAX_LENGTH = 100;
